import React, { useState, useEffect, useRef } from 'react';
import StatBox from '../ui/StatBox';
import useInterval from '../../hooks/useInterval';
import { playCorrectSound, playWrongSound, playMissSound, playGameOverSound } from '../../utils/audio';
import { api } from '../../utils/api';
import { User } from '../../types';
import { GameIntro, GameOver } from './GameShell';
import { ScorePops, useScorePops } from '../ui/ScorePops';

const WORD_COLORS = [
    { chip: 'bg-gradient-to-r from-pink-400 to-rose-500', pop: '#e11d48' },
    { chip: 'bg-gradient-to-r from-violet-400 to-purple-500', pop: '#7c3aed' },
    { chip: 'bg-gradient-to-r from-sky-400 to-blue-500', pop: '#2563eb' },
    { chip: 'bg-gradient-to-r from-emerald-400 to-teal-500', pop: '#059669' },
    { chip: 'bg-gradient-to-r from-amber-400 to-orange-500', pop: '#ea580c' },
];

type FallingWord = {
    id: number;
    text: string;
    x: number;
    y: number;
};

interface FallingWordsGameProps {
  user?: User;
}

const FallingWordsGame: React.FC<FallingWordsGameProps> = ({ user }) => {
    const [allWords, setAllWords] = useState<string[]>([]);
    const [fallingWords, setFallingWords] = useState<FallingWord[]>([]);
    const [typedText, setTypedText] = useState("");
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(5);
    const [level, setLevel] = useState(1);
    const [gameState, setGameState] = useState<'ready' | 'playing' | 'over'>('ready');
    const inputRef = useRef<HTMLInputElement>(null);
    const gameAreaRef = useRef<HTMLDivElement>(null);
    const gameLoopRef = useRef<number | null>(null);
    const { pops, pop } = useScorePops();

    useEffect(() => {
        api.get<string[]>('/api/v1/game/words?count=100').then(setAllWords);
    }, []);

    const startGame = () => {
        setGameState('playing');
        setScore(0);
        setLives(5);
        setLevel(1);
        setFallingWords([]);
        setTypedText("");
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    useEffect(() => {
        if (gameState === 'over' && user && user.user_id !== 'guest') {
            api.post('/api/v1/student/game-progress', { game_name: 'falling-words', high_score: score, increment_attempts: true }).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState]);

    useInterval(() => {
        if (gameState !== 'playing' || allWords.length === 0 || !gameAreaRef.current) return;
        
        const gameAreaWidth = gameAreaRef.current.offsetWidth;
        const availableWords = allWords.filter(w => w.length < 10);
        const text = availableWords[Math.floor(Math.random() * availableWords.length)];
        
        const newWord: FallingWord = {
            id: Date.now() + Math.random(),
            text,
            x: Math.random() * (gameAreaWidth - (text.length * 12)),
            y: -20,
        };
        setFallingWords(prev => [...prev, newWord]);
    }, Math.max(300, 2000 - level * 100));

    useEffect(() => {
        const gameLoop = () => {
            if (gameState !== 'playing' || !gameAreaRef.current) {
                if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
                return;
            }

            const gameAreaHeight = gameAreaRef.current.offsetHeight;
            if (gameAreaHeight === 0) {
                gameLoopRef.current = requestAnimationFrame(gameLoop);
                return;
            }
            const fallSpeed = 1 + level * 0.2;

            setFallingWords(currentWords => {
                const updatedWords = currentWords.map(word => ({ ...word, y: word.y + fallSpeed }));
                const survivedWords = updatedWords.filter(word => word.y < gameAreaHeight);
                
                const missedCount = updatedWords.length - survivedWords.length;
                if (missedCount > 0) {
                    setLives(l => {
                        const newLives = l - missedCount;
                        if (newLives <= 0) {
                            playGameOverSound();
                            setGameState('over');
                            return 0;
                        }
                        playMissSound();
                        return newLives;
                    });
                }
                return survivedWords;
            });

            gameLoopRef.current = requestAnimationFrame(gameLoop);
        };
        
        if (gameState === 'playing') gameLoopRef.current = requestAnimationFrame(gameLoop);
        return () => { if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current); };
    }, [gameState, level]);
    
    useEffect(() => {
        if(score > 0 && score % 50 === 0 && score / 50 === level) {
            setLevel(l => l + 1);
        }
    }, [score, level]);

    const submitWord = (wordToSubmit: string) => {
        const word = wordToSubmit.trim();
        if (!word) return;

        const foundWord = fallingWords.find(w => w.text === word);
        if (foundWord) {
            playCorrectSound();
            const area = gameAreaRef.current;
            if (area) pop(`+${foundWord.text.length}`, ((foundWord.x + foundWord.text.length * 7) / area.offsetWidth) * 100, (foundWord.y / area.offsetHeight) * 100, WORD_COLORS[Math.floor(foundWord.id) % WORD_COLORS.length].pop);
            setScore(s => s + foundWord.text.length);
            setFallingWords(ws => ws.filter(w => w.id !== foundWord.id));
        } else {
            playWrongSound();
            if (inputRef.current) {
                inputRef.current.classList.add('shake');
                setTimeout(() => inputRef.current?.classList.remove('shake'), 500);
            }
        }
        setTypedText("");
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newText = e.target.value;
        if (newText.endsWith(' ')) {
            submitWord(newText);
        } else {
            setTypedText(newText);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitWord(typedText);
        }
    };
    
    if (gameState === 'ready') return (
        <GameIntro icon="🌧️" title="Falling Words" skill="reaction time under pressure -- type fast before words pile up." accent="text-blue-600" button="bg-gradient-to-r from-sky-500 to-blue-600" onStart={startGame}>
            Words fall from the top of the screen. Type the highlighted word and press Space to clear it. You have 5 lives -- each missed word costs one. Survive as long as you can!
        </GameIntro>
    );
    if (gameState === 'over') return (
        <GameOver
            message={level >= 4 ? 'Super speedy fingers!' : 'Great try -- can you reach a higher level?'}
            stars={level >= 5 ? 3 : level >= 3 ? 2 : score > 0 ? 1 : 0}
            stats={[{ label: 'Final Score', value: score }, { label: 'Level Reached', value: level }]}
            button="bg-gradient-to-r from-sky-500 to-blue-600"
            onRestart={startGame}
        />
    );

    const currentTarget = fallingWords.find(w => w.text.startsWith(typedText))?.text;

    return (
        <div className="w-full" onClick={() => inputRef.current?.focus()}>
            <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
                <StatBox label="Score" value={score} />
                <StatBox label="Level" value={level} />
                 <div className="flex items-center gap-1 px-4 py-2 min-w-[88px] rounded-2xl bg-white shadow-md ring-2 ring-white/70">
                    <span className="text-3xl font-bold text-red-500 mr-2">{lives}</span>
                    <span className="text-2xl">❤️</span>
                </div>
            </div>
            <div ref={gameAreaRef} className="relative h-96 bg-gradient-to-b from-sky-300 via-sky-100 to-emerald-100 rounded-2xl overflow-hidden border-4 border-white shadow-inner">
                <span className="fun-float absolute top-4 left-[8%] text-5xl opacity-70" aria-hidden="true">☁️</span>
                <span className="fun-float absolute top-12 right-[12%] text-6xl opacity-60" style={{ animationDelay: '2s' }} aria-hidden="true">☁️</span>
                <span className="fun-float absolute top-2 left-[48%] text-4xl opacity-50" style={{ animationDelay: '3.5s' }} aria-hidden="true">☁️</span>
                <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-emerald-400 to-emerald-300" aria-hidden="true" />
                {fallingWords.map(word => {
                    const dangerRatio = gameAreaRef.current ? word.y / gameAreaRef.current.offsetHeight : 0;
                    const isDanger = dangerRatio > 0.7;
                    const isTarget = currentTarget === word.text;
                    const c = WORD_COLORS[Math.floor(word.id) % WORD_COLORS.length];
                    return (
                    <div key={word.id}
                         className={`absolute px-3 py-1.5 rounded-full shadow-lg font-mono text-xl font-bold border-4 transition-colors ${isTarget ? 'bg-yellow-300 border-white text-gray-900 ring-4 ring-yellow-400' : isDanger ? 'bg-red-500 border-white text-white animate-pulse' : `${c.chip} border-white text-white`}`}
                         style={{ transform: `translate(${word.x}px, ${word.y}px) scale(${isTarget ? 1.12 : 1})` }}>
                       {word.text}
                    </div>
                    );
                })}
                <ScorePops pops={pops} />
            </div>
            <input
                ref={inputRef}
                type="text"
                value={typedText}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-1/2 mx-auto mt-4 block p-2 text-center text-2xl font-mono font-bold border-4 border-sky-300 rounded-full shadow-md focus:border-blue-500 focus:ring-blue-500"
                autoFocus
            />
        </div>
    );
};

export default FallingWordsGame;